import { useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import {
  AutoSizer,
  Column,
  InfiniteLoader,
  Size,
  Table,
  CellMeasurerCache,
  TableHeaderRenderer,
  defaultTableCellRenderer,
  TableRowProps,
  TableCellRenderer,
  TableCellProps,
} from 'react-virtualized';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import {
  applicationTableConfigSelector,
  filterColumnsSelector,
  teamConfigSelector,
  sharingConfigSelector,
  widgetConfigSelector,
  sortSelector,
  filterSelector,
  isFilteringSelector,
  accessTokenTeamSelector,
  userRoleSelector,
} from '@algoan/dashboard/store/selectors';
import { getValueByPath, LiteralObject, isAnonymousCustomer } from '@algoan/dashboard/utils';
import { Spinner, SortIndicator } from '../../shared-components/atomic-components';
import { HighlightedSearch } from '../../shared-components';
import { messages } from '../application/application-messages';
import {
  customerListErrorSelector,
  customerListSelector,
  loadableCustomerNumberSelector,
  isSortingSelector,
  isCustomerListLoadingSelector,
} from './customer-table-selectors';
import { Customer, State } from '@algoan/dashboard/model';
import { fetchNextCustomers, sortCustomerList } from '@algoan/dashboard/store/thunks';
import { addAlpha } from '@algoan/deep-dive/theme';
import {
  cellSpacing,
  headerMargin,
  sectionSpacing,
  rowHeight,
  ExtendedTableProps,
  customerHasAnalysis,
  invitationColumnWidth,
  spacerWidth,
} from './customer-table-utils';
import {
  TableRow,
  StateCell,
  CreatedAtCell,
  GroupCell,
  PartnersCell,
  CurrentStateCell,
  SpacerCell,
  InvitationCell,
  PartnersCellProps,
  RowDataProps,
} from './customer-table-sub-components';
import { Tooltip, TooltipPosition } from '@algoan/deep-dive/tooltip';

const headerRowRenderer: TableHeaderRenderer = ({ dataKey, label, sortBy, sortDirection }) => {
  return (
    <>
      <span title={typeof label === 'string' ? label : ''}>{label}</span>
      <SortIndicator sortDirection={sortBy === dataKey ? sortDirection : undefined} />
    </>
  );
};

export const CustomerDataTable: React.FC = () => {
  const customerList = useSelector(customerListSelector);
  const error = useSelector(customerListErrorSelector);
  const areCustomersFetching = useSelector(isCustomerListLoadingSelector);
  const loadableCustomerNumber = useSelector(loadableCustomerNumberSelector);
  const sortData = useSelector(sortSelector);
  const applicationTableConfig = useSelector(applicationTableConfigSelector);
  const team = useSelector(accessTokenTeamSelector);
  const teamConfigInformation = useSelector(teamConfigSelector);
  const recipients = useSelector(sharingConfigSelector)?.recipients;
  const filterData = useSelector(filterSelector);
  const filterColumns = useSelector(filterColumnsSelector);
  const isSorting = useSelector(isSortingSelector);
  const isFiltering = useSelector(isFilteringSelector);
  const dispatch = useDispatch();
  const intl = useIntl();
  const widgetConfig = useSelector(widgetConfigSelector);
  const { isInitiator } = useSelector(userRoleSelector);

  const invitationEnabled = widgetConfig?.invitation;

  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: rowHeight,
    }),
  );

  const showTeamColumnAndFilter = !team && teamConfigInformation;

  const isRowLoaded = ({ index }: { index: number }): boolean => index < customerList.length - 1;
  const loadMoreRows = async (): Promise<void> => {
    if (!areCustomersFetching) {
      await dispatch(fetchNextCustomers());
    }
  };

  const isNewWithAnalysis = (customer: Customer): boolean =>
    customer.dashboard.currentState === State.NEW && customerHasAnalysis(customer);

  const indexOfLastNewApplicationWithAnalysis = customerList.findIndex((customer) => !isNewWithAnalysis(customer)) - 1;

  const highlightedCellRenderer: TableCellRenderer = (props) => (
    <HighlightedSearch cellData={(props.cellData as string) ?? ''} wordToSearch={filterData.inputValue ?? ''} />
  );

  const shouldHighlightColumn = (path: string): boolean => filterColumns.includes(path) && !!filterData.inputValue;

  useEffect(() => {
    cache.current.clearAll();
  });

  const handleTableRow = useCallback((props: TableRowProps): React.ReactNode => {
    return <TableRow {...props} />;
  }, []);

  const handleStateCell = useCallback((props: RowDataProps) => {
    return <StateCell {...props} />;
  }, []);

  const handleCreatedAtCell = useCallback((props: TableCellProps) => {
    return <CreatedAtCell {...props} />;
  }, []);

  const handleGroupCell = useCallback((props: RowDataProps) => {
    return <GroupCell rowData={props.rowData} />;
  }, []);

  const handlePartnersCell = useCallback((props: Omit<PartnersCellProps, 'cache'>) => {
    return <PartnersCell {...props} cache={cache} />;
  }, []);

  const handleCurrentStateCell = useCallback((props: RowDataProps) => {
    return <CurrentStateCell {...props} />;
  }, []);

  const handleInvitationCell = useCallback((props: RowDataProps) => {
    return <InvitationCell {...props} />;
  }, []);

  const handleSpacerCell = useCallback(() => {
    return <SpacerCell />;
  }, []);

  return (
    <S.TableContainer>
      {((areCustomersFetching && customerList.length === 0) || isSorting || isFiltering) && (
        <S.Overlay>
          <Spinner />
        </S.Overlay>
      )}
      {error && error === 'noCustomerFound' && (
        <S.NoResultsRow data-testid="no-customer-found">{intl.formatMessage(messages.noCustomerFound)}</S.NoResultsRow>
      )}
      <InfiniteLoader isRowLoaded={isRowLoaded} loadMoreRows={loadMoreRows} rowCount={loadableCustomerNumber}>
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer>
            {({ height, width }: Size) => {
              const firstColumnWidth = 50;
              const lastColumnWidth = 130;

              const totalFixedWidthColumns =
                firstColumnWidth + lastColumnWidth + (invitationEnabled ? invitationColumnWidth + spacerWidth : 0);

              const additionalColumns = applicationTableConfig?.additionalColumns ?? [];

              const nbOfColumns = additionalColumns.length + (invitationEnabled ? 6 : 4);
              const columnsWithFixedWidth = invitationEnabled ? 4 : 2;
              const otherColumnWidth = (width - totalFixedWidthColumns) / (nbOfColumns - columnsWithFixedWidth);

              return (
                <S.Table
                  ref={registerChild}
                  height={height - headerMargin} // CSS margins are not taken into account by react-virtualized computed height
                  width={width}
                  headerHeight={rowHeight}
                  rowCount={customerList.length}
                  rowHeight={({ index }: { index: number }) => {
                    const rowHeight = cache.current.rowHeight({ index: index });
                    return index === indexOfLastNewApplicationWithAnalysis ? rowHeight + sectionSpacing : rowHeight;
                  }}
                  rowGetter={({ index }: { index: number }) => customerList[index]}
                  gridClassName="table-body"
                  rowClassName={({ index }: { index: number }) => {
                    const isFirstRow = index === 0;
                    const isLastRow = index === customerList.length - 1;
                    const isLastNewApplicationWithAnalysis = index === indexOfLastNewApplicationWithAnalysis;
                    const isRowAfterLastNewApplicationWithAnalysis =
                      index === indexOfLastNewApplicationWithAnalysis + 1;

                    let style = '';
                    style += isFirstRow ? 'first-row ' : '';
                    if (index <= indexOfLastNewApplicationWithAnalysis) {
                      style += filterData.inputValue ? '' : 'table-row-new-with-analysis ';
                      style += isLastNewApplicationWithAnalysis ? 'table-row-with-delimiter ' : '';
                    } else if (isRowAfterLastNewApplicationWithAnalysis) {
                      style += 'table-row-after-delimiter ';
                    }
                    style += index % 2 === 1 ? 'table-row-even ' : 'table-row ';

                    if (invitationEnabled) {
                      if (
                        (isFirstRow && isLastNewApplicationWithAnalysis) ||
                        (isRowAfterLastNewApplicationWithAnalysis && isLastRow)
                      ) {
                        style += 'single-row-with-border ';
                      } else if (isFirstRow || isRowAfterLastNewApplicationWithAnalysis) {
                        style += 'first-row-with-border ';
                      } else if (isLastNewApplicationWithAnalysis || isLastRow) {
                        style += 'last-row-with-border ';
                      }
                    }

                    style += isAnonymousCustomer(customerList[index]) ? 'anonymous-customer' : 'table-hover';
                    return style;
                  }}
                  invitationEnabled={invitationEnabled}
                  isInitiator={isInitiator}
                  onRowClick={({ rowData: customer, index }: { rowData: Customer; index: number }) => {
                    logAmplitudeEvent(AmplitudeEvent.NAVIGATION_SELECT, {
                      folderNumber: customer.customIdentifier,
                      folderState: customer.dashboard.currentState,
                      rowNumber: index,
                    });
                  }}
                  onRowsRendered={onRowsRendered}
                  sort={() => {
                    dispatch(sortCustomerList());
                  }}
                  sortBy={sortData.sortBy}
                  sortDirection={sortData.sortDirection}
                  hasRows={!error}
                  rowRenderer={handleTableRow}
                >
                  <Column
                    label={intl.formatMessage(messages.stateColumn)}
                    width={firstColumnWidth}
                    dataKey="numberOfAnalyses"
                    className="cell centered-content"
                    headerClassName="column-header"
                    disableSort
                    cellRenderer={handleStateCell}
                  />
                  <Column
                    label={intl.formatMessage(messages.createdAtColumn)}
                    width={otherColumnWidth}
                    dataKey="dashboard.createdAt"
                    className="cell right-aligned-content"
                    headerClassName="column-header"
                    cellRenderer={handleCreatedAtCell}
                    headerRenderer={headerRowRenderer}
                  />
                  <Column
                    label={intl.formatMessage(messages.referenceColumn)}
                    width={otherColumnWidth}
                    dataKey="customIdentifier"
                    className="cell"
                    headerClassName="column-header"
                    disableSort
                    cellRenderer={(cellProps) =>
                      shouldHighlightColumn('customIdentifier')
                        ? highlightedCellRenderer(cellProps)
                        : defaultTableCellRenderer(cellProps)
                    }
                  />
                  {additionalColumns.map((column) => {
                    const shouldBeHighlighted = shouldHighlightColumn(column.path);
                    return (
                      <Column
                        key={column.path}
                        label={column.label}
                        width={otherColumnWidth}
                        dataKey={column.path}
                        className="cell"
                        headerClassName="column-header"
                        cellDataGetter={({ rowData: customer }: { rowData: Customer }) =>
                          getValueByPath(column.path, customer as unknown as LiteralObject)
                        }
                        disableSort
                        cellRenderer={(props) => {
                          return shouldBeHighlighted ? highlightedCellRenderer(props) : defaultTableCellRenderer(props);
                        }}
                      />
                    );
                  })}
                  {showTeamColumnAndFilter && (
                    <Column
                      label={intl.formatMessage(messages.teamColumn)}
                      width={otherColumnWidth}
                      id="team"
                      dataKey="team"
                      className="cell"
                      headerClassName="column-header"
                      disableSort
                      cellRenderer={handleGroupCell}
                    />
                  )}
                  {recipients && (
                    <Column
                      label={intl.formatMessage(messages.partnersColumn)}
                      width={otherColumnWidth}
                      dataKey="shares"
                      className="cell"
                      headerClassName="column-header"
                      cellRenderer={handlePartnersCell}
                    />
                  )}
                  <Column
                    label={intl.formatMessage(messages.statusColumn)}
                    width={lastColumnWidth}
                    dataKey="dashboard.currentState"
                    className="cell centered-content"
                    headerClassName="column-header"
                    disableSort
                    cellRenderer={handleCurrentStateCell}
                  />
                  {invitationEnabled && (
                    <Column
                      width={spacerWidth}
                      dataKey="spacer"
                      className="spacer"
                      headerClassName="header-spacer"
                      disableSort
                      cellRenderer={handleSpacerCell}
                    />
                  )}
                  {invitationEnabled && (
                    <Column
                      width={invitationColumnWidth}
                      dataKey="invitation"
                      className="cell centered-content invitation-cell"
                      headerClassName="column-header"
                      disableSort
                      cellRenderer={handleInvitationCell}
                    />
                  )}
                </S.Table>
              );
            }}
          </AutoSizer>
        )}
      </InfiniteLoader>
      <Tooltip id="anonymous-folder-tooltip" position={TooltipPosition.CURSOR} />
      <Tooltip id="partner-tooltip" />
      <Tooltip id="state-tooltip" />
    </S.TableContainer>
  );
};

const S = {
  Overlay: styled.div`
    background: rgba(255, 255, 255, 0.9);
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: flex-start;

    & > :first-child {
      margin-top: 60px;
    }
  `,
  NoResultsRow: styled.div`
    background: ${(props) => props.theme.secondary.chalk};
    color: ${(props) => props.theme.secondary.softBlue2};
    border-radius: 4px;
    top: 48px;
    width: 100%;
    font-size: 12px;
    line-height: 30px;
    height: 30px;
    position: absolute;
    text-align: center;
  `,
  TableContainer: styled.div`
    flex: 1;
    margin-top: 8px;
    min-height: 420px;
  `,
  Table: styled(Table as unknown as React.FC<ExtendedTableProps>)`
    font-size: 12px;
    line-height: 15px;
    color: ${(props) => props.theme.algoanBlue};

    a:visited,
    a:link {
      color: ${(props) => props.theme.algoanBlue};
    }

    .ReactVirtualized__Grid__innerScrollContainer {
      ${({ invitationEnabled, theme }) =>
        invitationEnabled
          ? ''
          : `
              border-bottom: ${addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;
      `}
      border-radius: 0 0 4px 4px;
    }

    .table-body {
      margin-top: ${headerMargin}px;
      outline: none;
      border-radius: 4px;

      overflow: auto;
      max-height: 600px;

      ${(props) =>
        props.hasRows
          ? `
           background:
            /* Shadow covers */
            linear-gradient(white 30%, rgba(255,255,255,0)),
            linear-gradient(rgba(255,255,255,0), white 70%) 0 100%,

            /* Shadows */
            linear-gradient(rgba(0, 70, 107, 0.06), rgba(0,0,0,0)),
            linear-gradient(rgba(0,0,0,0), rgba(0, 70, 107, 0.06)) 0 100%;
          background-repeat: no-repeat;
          background-size: 100% 40px, 100% 40px, 100% 6px, 100% 6px;

          background-attachment: local, local, scroll, scroll;
`
          : ''}

      .first-row {
        ${({ invitationEnabled, theme }) =>
          invitationEnabled ? '' : `border-top: ${addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;`}
      }

      .table-row-with-delimiter {
        height: ${rowHeight}px !important; // we need to override height computed and injected by react-virtualized

        ${({ invitationEnabled, theme }) =>
          invitationEnabled
            ? ''
            : `
                border-bottom: ${addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;
                border-radius: 0 0 4px 4px;
        `}
      }

      .table-row-after-delimiter {
        ${({ invitationEnabled, theme }) =>
          invitationEnabled
            ? ''
            : `
                border-top: ${addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;
                border-radius: 4px 4px 0 0;
        `}
      }

      .first-row-with-border {
        & > div:first-child {
          border-radius: 4px 0 0 0;
        }

        & > div:nth-last-child(3) {
          border-radius: 0 4px 0 0;
        }

        & > div:not(:nth-last-child(1)):not(:nth-last-child(2)) {
          border-top: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;
        }

        & > div:last-child {
          border-top: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;
          border-radius: 4px 4px 0 0;
        }
      }

      .last-row-with-border {
        & > div:first-child {
          border-radius: 0 0 0 4px;
        }

        & > div:nth-last-child(3) {
          border-radius: 0 0 4px 0;
        }

        & > div:not(:nth-last-child(1)):not(:nth-last-child(2)) {
          border-bottom: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} 2px solid;
        }

        & > div:last-child {
          border-bottom: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} 2px solid;
          border-radius: 0 0 4px 4px;
        }
      }

      .single-row-with-border {
        & > div:not(:nth-last-child(2)) {
          height: calc(100% - 2px) !important;
        }

        & > div:first-child {
          border-radius: 4px 0 0 4px;
        }

        & > div:nth-last-child(3) {
          border-radius: 0 4px 4px 0;
        }

        & > div:not(:nth-last-child(1)):not(:nth-last-child(2)) {
          border-top: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;
          border-bottom: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} 2px solid;
        }

        & > div:last-child {
          border: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;
          border-bottom: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} 2px solid;
          border-radius: 4px 4px 4px 4px;
        }
      }

      .table-row-even {
        outline: none;
      }

      .table-row {
        // the closest equivalent to chalk with a transparent background which is required for the shadow effect
        background-color: rgba(137, 158, 168, 0.05);
        outline: none;
      }

      .table-row-new-with-analysis {
        font-weight: bold;
      }

      .table-hover {
        &:hover:not(:has(> div.invitation-cell:hover)):not(:has(> div.spacer:hover)) {
          ${({ isInitiator, theme }) =>
            isInitiator
              ? ''
              : `
                  & > div:not(:nth-last-child(1)):not(:nth-last-child(2)) {
                    background-color: ${addAlpha(theme.algoanBlue, 0.05)};
                  }
                  cursor: pointer;
          `}
        }
      }

      .anonymous-customer {
        color: ${(props) => props.theme.secondary.softBlue1};
      }
    }

    .column-header,
    .cell {
      outline: none;
      height: 100%;
      border-right: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;
      min-width: 10px;
      display: flex;
      align-items: center;
      padding: 0 10px;
      margin: 0;
      font-weight: inherit;

      &:first-child {
        border-left: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;

        .table-row {
          &:first-child {
            border-radius: 4px 0 0 0px;
          }
        }
      }

      &:last-child {
        border-right: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;

        .table-row {
          &:first-child {
            border-radius: 4px 0 0 0px;
          }
        }
      }
    }

    .centered-content {
      justify-content: center;
    }

    .right-aligned-content {
      justify-content: center;
    }

    .column-header {
      background-color: ${(props) => props.theme.iceBlue};
      justify-content: center;
      text-align: center;

      &:first-child {
        border-radius: 4px 0 0 4px;
        border-left: unset;
      }

      &:last-child {
        border-radius: 0 4px 4px 0;
        border-right: unset;
      }

      span {
        font-weight: bold;
        white-space: inherit;
        text-transform: lowercase;

        &:first-letter {
          text-transform: uppercase;
        }
      }
    }

    .invitation-cell {
      border-left: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} ${cellSpacing}px solid;
    }

    .header-spacer {
      height: 100%;
      min-width: 8px;
      margin: 0;
      background-color: ${(props) => props.theme.iceBlue};
    }

    .spacer {
      height: 100%;
      // min-width = spacerWidth - cellSpacing because the invitation cell has a border-left
      min-width: ${spacerWidth - cellSpacing}px;
      margin: 0;
      background-color: white;
      cursor: default !important;
    }
  `,
};
